import { KnowledgeBase } from '../data/database';
import { vectorStoreService } from '../services/vector/vectorStore';

export async function findRelevantInfo(query: string, database: KnowledgeBase) {
  // Use vector search for semantic similarity
  const vectorResults = await vectorStoreService.searchSimilarContent(query, 3);
  
  // Extract product and sector information from vector results
  const relevantProducts = database.products.filter(product => 
    vectorResults.some(result => 
      result.content.toLowerCase().includes(product.name.toLowerCase())
    )
  );

  const relevantSectors = database.sectors.filter(sector =>
    vectorResults.some(result => 
      result.content.toLowerCase().includes(sector.name.toLowerCase())
    )
  );

  // Fallback to keyword search if vector search doesn't yield results
  if (relevantProducts.length === 0 && relevantSectors.length === 0) {
    const keywords = query.toLowerCase().split(' ');
    
    // Search through products
    const keywordProducts = database.products.filter(product => 
      keywords.some(keyword => 
        product.name.toLowerCase().includes(keyword) ||
        product.description.toLowerCase().includes(keyword) ||
        product.applications.some(app => app.toLowerCase().includes(keyword))
      )
    );

    // Search through sectors
    const keywordSectors = database.sectors.filter(sector =>
      keywords.some(keyword =>
        sector.name.toLowerCase().includes(keyword) ||
        sector.description.toLowerCase().includes(keyword) ||
        sector.applications.some(app => app.toLowerCase().includes(keyword))
      )
    );

    return {
      products: keywordProducts,
      sectors: keywordSectors,
      generalInfo: keywords.some(keyword =>
        database.generalInfo.companyDescription.toLowerCase().includes(keyword)
      ) ? database.generalInfo : null
    };
  }

  return {
    products: relevantProducts,
    sectors: relevantSectors,
    generalInfo: vectorResults.some(result => 
      result.content.toLowerCase().includes("company description")
    ) ? database.generalInfo : null
  };
}

export function shouldAskFollowUp(content: string, info: any): boolean {
  // Check if the message contains product-related questions
  const productKeywords = [
    'kiosk', 'display', 'scherm', 'prijs', 'specificaties',
    'etalage', 'winkel', 'aanbiedingen', 'informatie'
  ];

  const hasProductQuestion = productKeywords.some(keyword => 
    content.toLowerCase().includes(keyword)
  );

  // Check if we found relevant product info
  const hasProductInfo = info.products && info.products.length > 0;

  // Ask follow-up if it's a product question but we need more context
  return hasProductQuestion && !hasProductInfo;
}

export function getFollowUpQuestion(content: string, language: 'nl' | 'en'): string {
  // Determine appropriate follow-up based on context
  if (content.toLowerCase().includes('kiosk') || 
      content.toLowerCase().includes('display')) {
    return language === 'nl'
      ? 'Hoe zou u de kiosk willen monteren? We hebben opties voor:\n\n- Staand op een voetsteun\n- Bevestiging aan de muur\n- Montage aan het plafond\n\nDit helpt mij om de beste oplossing voor uw situatie te adviseren.'
      : 'How would you like to mount the kiosk? We have options for:\n\n- Standing on a pedestal\n- Wall mounting\n- Ceiling mounting\n\nThis helps me recommend the best solution for your situation.';
  }

  // Default follow-up for other cases
  return language === 'nl'
    ? 'Kunt u mij wat meer vertellen over waar u de kiosk voor wilt gebruiken? Dit helpt mij om de beste oplossing voor u te vinden.'
    : 'Could you tell me more about how you plan to use the kiosk? This helps me find the best solution for you.';
}